import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { StringListEditor, ObjectListEditor } from "@/components/site/ListInputs";
import { useAuth } from "@/context/AuthContext";
import { RichText, RichTextView, plainToHtml } from "@/components/cms/RichText";
import { MediaPicker } from "@/components/cms/MediaPicker";
import { BackgroundEditor } from "@/components/cms/BackgroundEditor";
import { Loader2, Save, ArrowLeft, ExternalLink, TrendingUp } from "lucide-react";

const NAVY = "#1C4358";
const APPROVAL = ["draft", "approved", "archived"];
const ic = "text-[15px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0";
const F = ({ label, children }) => (<div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">{label}</Label>{children}</div>);

export default function ReverseSearchEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const readOnly = user?.role !== "super_admin";
  const [pg, setPg] = useState(null);
  const [stats, setStats] = useState(null);
  const [lanes, setLanes] = useState([]);
  const [keywords, setKeywords] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, ln] = await Promise.all([api.get(`/site/reverse-search/${id}`), api.get("/site/service-lanes")]);
      setPg(d.data.page); setStats(d.data.stats);
      setKeywords((d.data.page.seo_keywords || []).join(", "));
      setLanes((ln.data.items || []).map((l) => ({ slug: l.slug, name: l.name })));
    } catch { toast.error("Could not load page."); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setPg((p) => ({ ...p, [k]: v }));
  const setAb = (k, v) => setPg((p) => ({ ...p, ab: { ...(p.ab || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        admin_title: pg.admin_title, slug: pg.slug, matched_service_lane: pg.matched_service_lane || null,
        hero_image_url: pg.hero_image_url, hero_image_alt: pg.hero_image_alt, hero_background: pg.hero_background || null,
        hero_headline: pg.hero_headline, hero_subheadline: pg.hero_subheadline,
        reassurance: pg.reassurance, problem_explanation: pg.problem_explanation, problem_explanation_html: pg.problem_explanation_html,
        we_help_with: pg.we_help_with,
        common_questions: pg.common_questions, matching_lane_copy: pg.matching_lane_copy, matching_lane_copy_html: pg.matching_lane_copy_html,
        trust_points: pg.trust_points,
        cta_primary_label: pg.cta_primary_label, cta_call_label: pg.cta_call_label, cta_chat_label: pg.cta_chat_label,
        seo_title: pg.seo_title, seo_description: pg.seo_description,
        seo_keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean),
        ab: pg.ab, active: pg.active, approval_status: pg.approval_status,
        include_in_sitemap: pg.include_in_sitemap, noindex: pg.noindex,
      };
      const { data } = await api.put(`/site/reverse-search/${id}`, payload);
      setPg(data.page); toast.success("Saved. Public page updated."); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;
  if (!pg) return null;

  return (
    <div className="max-w-3xl mx-auto pb-24" data-testid="reverse-editor">
      <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-white/95 backdrop-blur border-b border-[#E6EDF2] flex items-center justify-between gap-3">
        <button onClick={() => navigate("/site/reverse-search")} className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#1C4358] hover:opacity-70"><ArrowLeft size={16} /> Pages</button>
        <div className="flex items-center gap-2">
          <a href={`/find/${pg.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#1C4358] px-3 h-10 rounded-lg hover:bg-[#1C4358]/6">Preview <ExternalLink size={15} /></a>
          {!readOnly && <Button onClick={save} disabled={saving} data-testid="reverse-save-btn" className="gap-1.5 bg-[#9C1E22] hover:bg-[#861A1D] text-white">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</Button>}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4">
        <h1 className="text-xl font-display font-bold text-[#1C4358]">{pg.admin_title}</h1>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[rgba(28,67,88,0.10)] text-[#1C4358]">GLOBAL</span>
      </div>
      {readOnly && <p className="text-[13px] text-[#9C1E22] mt-1">Read-only — only the Super Admin can edit global reverse search content.</p>}
      {stats && <p className="text-[13px] text-[#2F7D5A] mt-1 flex items-center gap-1"><TrendingUp size={13} /> {stats.total_leads} leads · variations: {Object.entries(stats.by_variation || {}).map(([k, v]) => `${k}: ${v}`).join(", ") || "—"}</p>}

      <Accordion type="multiple" defaultValue={["basics", "hero"]} className="mt-4 space-y-3">
        <AccordionItem value="basics" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="font-display font-semibold text-[#1C4358]">Status, SEO & flags</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <div className="grid sm:grid-cols-2 gap-3">
              <F label="Title"><Input data-testid="re-title" className={ic} value={pg.admin_title || ""} disabled={readOnly} onChange={(e) => set("admin_title", e.target.value)} /></F>
              <F label="Slug"><Input data-testid="re-slug" className={ic} value={pg.slug || ""} disabled={readOnly} onChange={(e) => set("slug", e.target.value)} /></F>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <F label="Matched service lane">
                <Select value={pg.matched_service_lane || "none"} disabled={readOnly} onValueChange={(v) => set("matched_service_lane", v === "none" ? null : v)}>
                  <SelectTrigger className={ic} data-testid="re-lane"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{lanes.map((l) => <SelectItem key={l.slug} value={l.slug}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Approval">
                <Select value={pg.approval_status} disabled={readOnly} onValueChange={(v) => set("approval_status", v)}>
                  <SelectTrigger className={ic} data-testid="re-approval"><SelectValue /></SelectTrigger>
                  <SelectContent>{APPROVAL.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-[14px]"><Switch checked={!!pg.active} disabled={readOnly} onCheckedChange={(v) => set("active", v)} data-testid="re-active" /> Active (public)</label>
              <label className="flex items-center gap-2 text-[14px]"><Switch checked={!!pg.include_in_sitemap} disabled={readOnly} onCheckedChange={(v) => set("include_in_sitemap", v)} data-testid="re-sitemap" /> Include in sitemap</label>
              <label className="flex items-center gap-2 text-[14px]"><Switch checked={!!pg.noindex} disabled={readOnly} onCheckedChange={(v) => set("noindex", v)} data-testid="re-noindex" /> noindex</label>
            </div>
            {readOnly ? (
              pg.hero_image_url ? <img src={pg.hero_image_url} alt={pg.hero_image_alt || ""} className="h-32 rounded-lg border border-[#E6EDF2] object-cover" /> : null
            ) : (
              <MediaPicker label="Hero image" testid="reverse-hero-media"
                value={pg.hero_image_url || ""} alt={pg.hero_image_alt || ""}
                onUrlChange={(url) => set("hero_image_url", url)} onAltChange={(alt) => set("hero_image_alt", alt)} />
            )}
            <F label="SEO title"><Input data-testid="re-seo-title" className={ic} value={pg.seo_title || ""} disabled={readOnly} onChange={(e) => set("seo_title", e.target.value)} /></F>
            <F label="SEO description"><Textarea rows={2} className={ic} value={pg.seo_description || ""} disabled={readOnly} onChange={(e) => set("seo_description", e.target.value)} /></F>
            <F label="SEO keywords (comma separated)"><Input className={ic} value={keywords} disabled={readOnly} onChange={(e) => setKeywords(e.target.value)} /></F>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="hero" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="font-display font-semibold text-[#1C4358]">Hero & reassurance</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <F label="Headline"><Textarea data-testid="re-headline" rows={2} className={ic} value={pg.hero_headline || ""} disabled={readOnly} onChange={(e) => set("hero_headline", e.target.value)} /></F>
            <F label="Subheadline"><Textarea rows={2} className={ic} value={pg.hero_subheadline || ""} disabled={readOnly} onChange={(e) => set("hero_subheadline", e.target.value)} /></F>
            <F label="Reassurance line"><Input className={ic} value={pg.reassurance || ""} disabled={readOnly} onChange={(e) => set("reassurance", e.target.value)} /></F>
            <F label="Problem explanation"><Textarea rows={3} className={ic} value={pg.problem_explanation || ""} disabled={readOnly} onChange={(e) => set("problem_explanation", e.target.value)} /></F>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="content" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="font-display font-semibold text-[#1C4358]">Content & FAQs</AccordionTrigger>
          <AccordionContent className="space-y-5 pb-5">
            <StringListEditor label="What MovingMyMama helps with" items={pg.we_help_with} readOnly={readOnly} onChange={(v) => set("we_help_with", v)} testid="re-helps" />
            <StringListEditor label="Trust / proof points" items={pg.trust_points} readOnly={readOnly} onChange={(v) => set("trust_points", v)} />
            <F label="Matching service lane copy">
              {readOnly
                ? <RichTextView html={pg.matching_lane_copy_html || plainToHtml(pg.matching_lane_copy)} className="rounded-lg border border-[#E6EDF2] bg-[#F7FAFC] p-3 text-[14px]" />
                : <RichText testid="re-matching" value={pg.matching_lane_copy_html || plainToHtml(pg.matching_lane_copy)} onChange={(html) => set("matching_lane_copy_html", html)} />}
            </F>
            <ObjectListEditor label="Common family questions" items={pg.common_questions} readOnly={readOnly} onChange={(v) => set("common_questions", v)} testid="re-faqs"
              fields={[{ key: "q", label: "Question" }, { key: "a", label: "Answer", textarea: true }]} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="cta" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="font-display font-semibold text-[#1C4358]">CTAs</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <div className="grid sm:grid-cols-3 gap-3">
              <F label="Primary CTA"><Input className={ic} value={pg.cta_primary_label || ""} disabled={readOnly} onChange={(e) => set("cta_primary_label", e.target.value)} /></F>
              <F label="Call CTA"><Input className={ic} value={pg.cta_call_label || ""} disabled={readOnly} onChange={(e) => set("cta_call_label", e.target.value)} /></F>
              <F label="Chat CTA"><Input className={ic} value={pg.cta_chat_label || ""} disabled={readOnly} onChange={(e) => set("cta_chat_label", e.target.value)} /></F>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="ab" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="font-display font-semibold text-[#1C4358]">A/B test variants</AccordionTrigger>
          <AccordionContent className="space-y-5 pb-5">
            <p className="text-[13px] text-[#4B5563]">Add alternate versions. A random variant is shown per visit and tracked on the lead. The base value is always variant 0.</p>
            <StringListEditor label="Headline variants" items={pg.ab?.headline_variants || []} readOnly={readOnly} onChange={(v) => setAb("headline_variants", v)} testid="re-ab-headlines" placeholder="Alternate headline" />
            <StringListEditor label="CTA variants" items={pg.ab?.cta_variants || []} readOnly={readOnly} onChange={(v) => setAb("cta_variants", v)} placeholder="Alternate CTA label" />
            <StringListEditor label="Hero image variants (URLs)" items={pg.ab?.hero_image_variants || []} readOnly={readOnly} onChange={(v) => setAb("hero_image_variants", v)} placeholder="https://…" />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
